import * as tslib_1 from "tslib";
import scrollIntoView from 'scroll-into-view-if-needed';
import { m } from '../hyperscript';
import { AutocompleteChange, AutocompleteStore } from '../store/autocompletestore';
import { View } from '../ui/view';
var callFunctions = function () {
    var fns = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        fns[_i] = arguments[_i];
    }
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return fns.forEach(function (fn) { return fn && fn.apply(void 0, args); });
    };
};
var callHandlers = function () {
    var fns = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        fns[_i] = arguments[_i];
    }
    return function (event) {
        event.redraw = false;
        return callFunctions.apply(void 0, fns)(event);
    };
};
var normalizeArrowKey = function (event) {
    var key = event.key, keyCode = event.keyCode;
    if (keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0)
        return "Arrow" + key;
    return key;
};
var isOrContainsNode = function (parent, child) {
    return parent === child || (parent.contains && parent.contains(child));
};
var Autocomplete = /** @class */ (function (_super) {
    tslib_1.__extends(Autocomplete, _super);
    function Autocomplete() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.defaultStore = new AutocompleteStore();
        _this.items = [];
        _this.id = "autocomplete-" + Autocomplete.instanceCount++;
        _this.menuId = _this.id + "-menu";
        _this.labelId = _this.id + "-label";
        _this.inputId = _this.id + "-input";
        _this.buttonId = _this.id + "-button";
        _this.getItemId = function (index) { return _this.id + "-item-" + index; };
        _this.clearCallbacks = [];
        _this.avoidScrolling = false;
        _this.interacting = false;
        // -- Input -------------------
        _this.inputAttrs = function (attrs) {
            if (attrs === void 0) { attrs = {}; }
            var onkeydown = attrs.onkeydown, onblur = attrs.onblur, oninput = attrs.oninput, rest = tslib_1.__rest(attrs, ["onkeydown", "onblur", "oninput"]);
            var _a = _this.store, inputValue = _a.inputValue, isOpen = _a.isOpen, highlightedIndex = _a.highlightedIndex;
            var events = {
                oninput: callHandlers(oninput, _this.inputChange),
                onblur: callHandlers(oninput, _this.inputBlur),
                onkeydown: callHandlers(oninput, _this.keyDown)
            };
            return tslib_1.__assign({ 'aria-autocomplete': 'list', 'aria-activedescendant': isOpen && typeof highlightedIndex === 'number' && highlightedIndex >= 0
                    ? _this.getItemId(highlightedIndex)
                    : null, 'aria-controls': isOpen ? _this.menuId : null, 'aria-labelledby': _this.labelId, autoComplete: 'off', value: inputValue, id: _this.inputId }, events, rest);
        };
        _this.inputChange = function (event) {
            var element = event.target;
            _this.dispatch({
                type: AutocompleteChange.InputChange,
                value: element.value
            });
        };
        _this.inputBlur = function (event) {
            var buttonIsActive = document &&
                !!document.activeElement &&
                !!document.activeElement.id &&
                document.activeElement.id == _this.buttonId;
            if (!_this.interacting && !buttonIsActive)
                _this.dispatch({ type: AutocompleteChange.InputBlur });
        };
        // -- Item -------------------
        _this.itemAttrs = function (attrs) {
            var item = attrs.item, onclick = attrs.onclick, onmousemove = attrs.onmousemove, onmousedown = attrs.onmousedown, onkeydown = attrs.onkeydown, rest = tslib_1.__rest(attrs, ["item", "onclick", "onmousemove", "onmousedown", "onkeydown"]);
            var highlightedIndex = _this.store.highlightedIndex;
            var index = _this.items.length;
            _this.items.push(item);
            var events = {
                onclick: callHandlers(onclick, function () { return _this.itemClick(index); }),
                onmousemove: callHandlers(onmousemove, function () { return _this.itemMouseMove(index); }),
                onkeydown: callHandlers(onkeydown, _this.keyDown)
            };
            var selected = highlightedIndex === index;
            var scrollTo = selected && {
                onupdate: function (vnode) {
                    if (_this.avoidScrolling || !_this.store.isOpen)
                        return;
                    scrollIntoView(vnode.dom, { behavior: 'smooth', scrollMode: 'if-needed' });
                }
            };
            return tslib_1.__assign({ id: _this.getItemId(index), role: 'option', 'aria-selected': selected }, events, scrollTo, rest);
        };
        // -- Label -------------------
        _this.labelAttrs = function (attrs) {
            if (attrs === void 0) { attrs = {}; }
            return tslib_1.__assign({ for: _this.inputId, id: _this.labelId }, attrs);
        };
        // -- Menu -------------------
        _this.menuAttrs = function (attrs) {
            if (attrs === void 0) { attrs = {}; }
            return tslib_1.__assign({ role: 'listbox', 'aria-labelledby': _this.labelId, id: _this.menuId }, attrs);
        };
        // -- Button -------------------
        _this.buttonAttrs = function (attrs) {
            if (attrs === void 0) { attrs = {}; }
            var onclick = attrs.onclick, onkeydown = attrs.onkeydown, onblur = attrs.onblur, rest = tslib_1.__rest(attrs, ["onclick", "onkeydown", "onblur"]);
            var isOpen = _this.store.isOpen;
            var events = {
                onclick: callHandlers(onclick, _this.buttonClick),
                onkeydown: callHandlers(onkeydown, _this.keyDown),
                onblur: callHandlers(onblur, _this.buttonBlur)
            };
            return tslib_1.__assign({ id: _this.buttonId, type: 'button', role: 'button', 'aria-label': isOpen ? 'close menu' : 'open menu', 'aria-haspopup': true }, events, rest);
        };
        _this.buttonClick = function (event) {
            var button = event.target;
            event.preventDefault();
            if (document.activeElement === document.body)
                button.focus();
            _this.dispatch({ type: AutocompleteChange.ButtonClick }, { async: true });
        };
        _this.buttonBlur = function (event) {
            var blurTarget = event.target;
            _this.schedule(function () {
                if (!_this.interacting &&
                    (document.activeElement == null ||
                        document.activeElement.id !== _this.inputId) &&
                    document.activeElement !== blurTarget)
                    _this.dispatch({ type: AutocompleteChange.ButtonBlur });
            });
        };
        // -- Key down handlers -------------------
        _this.keyDown = function (event) {
            switch (normalizeArrowKey(event)) {
                case 'ArrowUp':
                    return _this.keyArrowup(event);
                case 'ArrowDown':
                    return _this.keyArrowdown(event);
                case 'Escape':
                    return _this.keyEscape(event);
                case 'Enter':
                    return _this.keyEnter(event);
            }
        };
        return _this;
    }
    Object.defineProperty(Autocomplete.prototype, "store", {
        get: function () {
            return this.attrs.store || this.defaultStore;
        },
        enumerable: true,
        configurable: true
    });
    Autocomplete.prototype.onCreate = function (dom) {
        var _this = this;
        var onMouseDown = function () { return (_this.interacting = true); };
        var onMouseUp = function (event) {
            var target = event.target;
            var isOpen = _this.store.isOpen;
            _this.schedule(function () {
                _this.interacting = false;
                if (isOpen && !isOrContainsNode(dom, target))
                    _this.dispatch({ type: AutocompleteChange.MouseUp });
            });
        };
        dom.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);
        dom.addEventListener('touchstart', onMouseDown, { passive: true });
        window.addEventListener('touchend', onMouseUp);
        this.onRemove = function () {
            dom.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mouseup', onMouseUp);
            dom.removeEventListener('touchstart', onMouseDown);
            window.removeEventListener('touchend', onMouseUp);
            _this.clearCallbacks.forEach(function (fn) { return fn(); });
        };
    };
    Autocomplete.prototype.schedule = function (work, time) {
        var id = setTimeout(work, time);
        this.clearCallbacks.push(function () { return clearTimeout(id); });
    };
    Autocomplete.prototype.dispatch = function (action, options) {
        if (options === void 0) { options = {}; }
        var _a = this.attrs, _b = _a.itemToString, itemToString = _b === void 0 ? function (item) { return "" + item; } : _b, onselect = _a.onselect;
        var async = options.async;
        var wasSelected = this.store.selectedItem;
        this.store.dispatch(action, {
            items: this.items,
            itemToString: itemToString
        });
        var selectedItem = this.store.selectedItem;
        if (wasSelected != selectedItem)
            if (onselect)
                onselect(selectedItem);
        if (async)
            m.redraw();
        else
            m.redraw.sync();
    };
    Autocomplete.prototype.itemMouseMove = function (index) {
        var _this = this;
        var highlightedIndex = this.store.highlightedIndex;
        if (index === highlightedIndex)
            return;
        this.avoidScrolling = true;
        this.dispatch({
            type: AutocompleteChange.ItemMouseEnter,
            index: index
        });
        this.schedule(function () { return (_this.avoidScrolling = false); }, 250);
    };
    Autocomplete.prototype.itemClick = function (index) {
        var item = this.items[index];
        this.dispatch({
            type: AutocompleteChange.ItemClick,
            item: item
        });
    };
    Autocomplete.prototype.keyArrowdown = function (event) {
        event.preventDefault();
        var amount = event.shiftKey ? 5 : 1;
        this.dispatch({
            type: AutocompleteChange.KeyArrowDown,
            amount: amount,
            total: this.items.length
        });
    };
    Autocomplete.prototype.keyArrowup = function (event) {
        event.preventDefault();
        var amount = event.shiftKey ? -5 : -1;
        this.dispatch({
            type: AutocompleteChange.KeyArrowUp,
            amount: amount,
            total: this.items.length
        });
    };
    Autocomplete.prototype.keyEnter = function (event) {
        var _a = this.store, isOpen = _a.isOpen, highlightedIndex = _a.highlightedIndex;
        if (!isOpen || highlightedIndex === null)
            return;
        event.preventDefault();
        var item = this.items[highlightedIndex];
        // Todo: don't trigger for disabled items
        this.dispatch({
            type: AutocompleteChange.KeyEnter,
            item: item
        });
    };
    Autocomplete.prototype.keyEscape = function (event) {
        event.preventDefault();
        this.dispatch({
            type: AutocompleteChange.KeyEscape
        });
    };
    // -- View -------------------
    Autocomplete.prototype.render = function () {
        this.items = [];
        return this.children(tslib_1.__assign({}, this.store, { inputAttrs: this.inputAttrs, menuAttrs: this.menuAttrs, buttonAttrs: this.buttonAttrs, labelAttrs: this.labelAttrs, itemAttrs: this.itemAttrs }));
    };
    Autocomplete.instanceCount = 0;
    return Autocomplete;
}(View));
export { Autocomplete };
